import { useState, useEffect } from 'react';
import { useLanguage } from '../../providers/preferenceProvider';
import { useConfig } from '../../providers/configProvider';
import { useToast } from '../../providers/toastProvider';
import '../../css/overlay.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCoins, faCrown, faDoorClosed, faPen } from '@fortawesome/free-solid-svg-icons';
import { createRole, updateRole, deleteRole } from '../../api/role-api';
import { ACL } from '../../acls';

const EditRole = ({ isOpen, onClose, role, acls, rooms, createNew, onRefresh }) => {
  const { t } = useLanguage();
  const { isAuthorized } = useConfig();
  const { showToast, showActionToast, showLoading, hideLoading } = useToast();
  const [name, setName] = useState('');
  const [selectedAcls, setSelectedAcls] = useState([]);
  const [selectedRooms, setSelectedRooms] = useState([]);
  const [credits, setCredits] = useState('');
  const [fieldErrors, setFieldErrors] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    if (role) {
      setName(role.name);
      setSelectedAcls(role.Acls.map((acl) => acl.id));
      setSelectedRooms(role.Rooms.map((room) => room.id));
      setCredits(role.credits || 0);
    }
  }, [isOpen, role]);

  if (!isOpen) return null;

  const closeOverlay = () => {
    onClose();
  }

  const saveChange = () => {
    showLoading();

    const newErrors = [];
    if (!name) newErrors.push("name");
    setFieldErrors(newErrors);

    if (newErrors.length > 0) {
      showToast(t.toast.fill_all_fields, 'error');
      hideLoading();
      return;
    }

    if (createNew) {
      createRole(name?.trim(), selectedAcls, selectedRooms, credits).then(() => {
        onRefresh();
        hideLoading();
        closeOverlay();
        showToast(t.role + " " + t.toast.created_success, 'success');
      }).catch(() => {
        hideLoading();
      });
    } else {
      updateRole(role.id, name?.trim(), selectedAcls, selectedRooms, credits).then(() => {
        onRefresh();
        hideLoading();
        closeOverlay();
        showToast(t.role + " " + t.toast.updated_success, 'success');
      }).catch(() => {
        hideLoading();
      });
    }
  }

  const deleteConfirmation = () => {
    if (role.userCount > 0) {
      showToast(t.toast.role_not_empty_error, "error");
      return;
    }
    showActionToast(
      t.toast.confirm_delete_general.replace('{ph_name}', role.name),
      handleDeleteRole,
      null
    );
  }

  const handleDeleteRole = () => {
    showLoading();
    deleteRole(role.id).then(() => {
      onRefresh();
      closeOverlay();
      hideLoading();
      showToast(t.role + " " + t.toast.deleted_success, 'success');
    }).catch(() => {
      hideLoading();
    });
  }

  const toggleSelection = (id, setFn) => {
    setFn(prev =>
      prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
    );
  };

  function formatAclName(name) {
    return name
      .toLowerCase()
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  return (
    <div className="overlay-backdrop" onClick={onClose}>
      <div className="overlay-content" onClick={(e) => e.stopPropagation()}>
        <div className="overlay-header">{createNew ? t.add : t.edit} {t.role}</div>
        <div className="overlay-admin-form flex flex-col gap-4 p-4">

          <div className="flex flex-row items-center gap-4">
            <label htmlFor="name" className="w-32"><FontAwesomeIcon icon={faPen} /> {t.name}</label>
            <input id="name" name="name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t.name + "..."} className={"overlay-admin-input" + (fieldErrors.includes("name") ? " input-error" : "")} />
          </div>

          <h1 className='text-xl'><FontAwesomeIcon icon={faCrown}/> {t.edit + " " + t.rights}</h1>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {acls && acls.map((acl) => (
              <div key={acl.id} className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id={`acl-${acl.id}`}
                    checked={selectedAcls.includes(acl.id)}
                    onChange={() => toggleSelection(acl.id, setSelectedAcls)}
                  />
                  <label htmlFor={`acl-${acl.id}`}>{formatAclName(acl.name)}</label>
                </div>
                {acl.description && (
                  <span className="text-xs text-gray-500 ml-6">{acl.description}</span>
                )}
              </div>
            ))}
          </div>

          <h1 className='text-xl'><FontAwesomeIcon icon={faDoorClosed}/> {t.edit + " " + t.rooms}</h1>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {rooms && rooms.map((room) => (
              <div key={room.id} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id={`room-${room.id}`}
                  checked={selectedRooms.includes(room.id)}
                  onChange={() => toggleSelection(room.id, setSelectedRooms)}
                />
                <label htmlFor={`room-${room.id}`}>{room.name}</label>
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="credits"><FontAwesomeIcon icon={faCoins} /> {t.amount_of} {t.credits}</label>
            <input type="number" id="credits" name="credits" value={credits} onChange={(e) => setCredits(e.target.value)} placeholder={t.amount_of + " " + t.credits + "..."} className={"overlay-admin-input"} />
          </div>

        </div>
        <div className="flex flex-row gap-2 justify-between">
          <div className='flex flex-row gap-2 align-items-center'>
            {!createNew && (
              <button className="btn" onClick={deleteConfirmation} disabled={!isAuthorized(ACL.ROLE)}>{t.delete + " " + t.role}</button>
            )}
          </div>
          <div className="flex gap-2 justify-end">
            <button className="btn-close" onClick={closeOverlay}>{t.close}</button>
            <button className="btn" onClick={saveChange}>{t.confirm}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditRole;